/**
 * Pathname predicates shared by Session.tsx and the global axios interceptor.
 *
 * Two route categories opt out of strict auth handling:
 *
 *  - `isSessionEstablishmentExempt`: pages we render before a session
 *    exists (registration flows, password reset, verify-email, etc).
 *    Session.tsx skips its user-fetch on these and the interceptor must
 *    not bounce a 403 to login from these — there's no session to expire.
 *
 *  - `isOptionalAuthRoute`: `/pub/*` pages that can be visited
 *    anonymously OR while logged in. Same treatment: a 403 from a query
 *    fired by the page must not redirect to login because the page is
 *    valid for anonymous visitors.
 *
 * Keeping the predicates here lets the interceptor honor the same intent
 * Session.tsx expresses, instead of redirecting on every 403 and undoing
 * the route-aware gating upstream.
 */

export const isSessionEstablishmentExemptPath = (pathname: string): boolean =>
  pathname.startsWith('/invitation') ||
  pathname.startsWith('/register/provider/setup') ||
  pathname.startsWith('/register/provider/sourced') ||
  pathname.startsWith('/password-reset') ||
  pathname.includes('/password-reset/') ||
  pathname.startsWith('/verify-email') ||
  pathname === '/forgot-password' ||
  pathname === '/reset-password' ||
  pathname.startsWith('/swag') ||
  pathname.startsWith('/endorsement');

export const isOptionalAuthRoutePath = (pathname: string): boolean => pathname.startsWith('/pub');

/**
 * True when the current page tolerates an unauthenticated state, either
 * because the user hasn't established a session yet or because the route
 * is public-by-design. Used by the global axios interceptor to skip the
 * 403 → session-expired redirect.
 */
export const isAnonymousTolerantPath = (pathname: string): boolean =>
  isSessionEstablishmentExemptPath(pathname) || isOptionalAuthRoutePath(pathname);
